import { useState } from 'react';
import {
    Box, ButtonBase, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { AiLeadInsights, AiService, useAiStatus } from '@services/ai.service';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { AiInsightsResult } from './ai-insights-result';
import { AiAura } from './ai-aura';

/// O mesmo raio dos botões de ação do cabeçalho do lead.
const BUTTON_RADIUS = 9;

/// Rótulo do tópico no começo da linha ("O que o cliente quer:"); sem dígitos, para um "14:00" no texto não contar.
const TOPIC_LABEL = /^[^:\d\n]{1,40}:/;

interface Props {
    /// Sem conversa no Multicanal não há o que a IA ler.
    conversationId?: number | null;
    inboxId?: number | null;
    /// Conversa que já virou lead pode completar o cadastro dele.
    leadId?: string | null;
    onApplied?: () => void;
    /// No Multicanal ocupa a largura; no cabeçalho do lead fica do tamanho do texto.
    fullWidth?: boolean;
}

/// A IA sobre a conversa: um botão na tela e as funções num modal, para não ocupar o espaço do lead.
export const AiAssistant = ({ conversationId, inboxId, leadId, onApplied, fullWidth = true }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const configured = useAiStatus();

    const [open, setOpen] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const [summarizing, setSummarizing] = useState(false);
    const [insights, setInsights] = useState<AiLeadInsights | null>(null);
    const [reading, setReading] = useState(false);
    /// Troca a cada leitura: a lista de caixas começa marcada de novo.
    const [reads, setReads] = useState(0);

    // IA sem chave no servidor não aparece: o usuário não tem como ligá-la daqui.
    if (!configured || !conversationId) return null;

    const summarize = async () => {
        setSummarizing(true);

        try {
            const res = await new AiService().summarize(conversationId, inboxId);
            setSummary(res.data.data?.text || null);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSummarizing(false);
        }
    };

    const read = async () => {
        if (!leadId) return;

        setReading(true);

        try {
            const res = await new AiService().insights(leadId);
            setInsights(res.data.data);
            setReads((n) => n + 1);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setReading(false);
        }
    };

    const spinner = <CircularProgress size={13} color="inherit" />;

    const section = (
        title: string,
        help: string,
        action: React.ReactNode,
        result: React.ReactNode,
    ) => (
        <Box sx={{ border: `1px solid ${cc.border}`, borderRadius: '10px', p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: cc.textPrimary }}>
                    {title}
                </Typography>
                {action}
            </Box>

            {result ?? (
                <Typography sx={{ fontSize: 12, color: cc.textMuted, mt: 0.5 }}>{help}</Typography>
            )}
        </Box>
    );

    const resultBox = (children: React.ReactNode) => (
        <Box sx={{ mt: 1, p: '8px 10px', borderRadius: '8px', background: cc.accentSoftBg }}>{children}</Box>
    );

    return (
        <>
            <AiAura radius={BUTTON_RADIUS} darkMode={controller.darkMode}>
                <ButtonBase
                    onClick={() => setOpen(true)}
                    sx={{
                        width: fullWidth ? '100%' : 'auto',
                        px: fullWidth ? 0 : 1.9,
                        height: 36,
                        gap: 0.75,
                        whiteSpace: 'nowrap',
                        borderRadius: `${BUTTON_RADIUS}px`,
                        bgcolor: cc.cardBg,
                        color: cc.textPrimary,
                        fontSize: 12.5,
                        fontWeight: 700,
                        '&:hover': { bgcolor: cc.countBg },
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 17, color: cc.accent }} />
                    {t('ia.titulo')}
                </ButtonBase>
            </AiAura>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: cc.accent }} />
                    <Box component="span" sx={{ flex: 1 }}>{t('ia.titulo')}</Box>
                    <IconButton onClick={() => setOpen(false)} aria-label={t('general.fechar')} sx={{ color: cc.textSecondary }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
                    {section(
                        t('ia.secaoResumo'),
                        t('ia.resumoAjuda'),
                        <MDButton
                            size="small"
                            variant={summary ? 'text' : 'gradient'}
                            color="info"
                            onClick={summarize}
                            disabled={summarizing}
                            startIcon={summarizing ? spinner : undefined}
                        >
                            {summary ? t('ia.gerarDeNovo') : t('ia.botaoResumir')}
                        </MDButton>,
                        summary && resultBox(
                            <Typography
                                sx={{ fontSize: 13.5, color: cc.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}
                            >
                                {summary.split('\n').map((line, index) => {
                                    const label = TOPIC_LABEL.exec(line)?.[0];

                                    return (
                                        <Box key={index} component="span" sx={{ display: 'block' }}>
                                            {label && (
                                                <Box component="strong" sx={{ fontWeight: 700, color: cc.accent }}>
                                                    {label}
                                                </Box>
                                            )}
                                            {label ? line.slice(label.length) : line}
                                        </Box>
                                    );
                                })}
                            </Typography>
                        ),
                    )}

                    {leadId && section(
                        t('ia.secaoCompletar'),
                        t('ia.completarAjuda'),
                        <MDButton
                            size="small"
                            variant={insights ? 'text' : 'gradient'}
                            color="info"
                            onClick={read}
                            disabled={reading}
                            startIcon={reading ? spinner : undefined}
                        >
                            {insights ? t('ia.gerarDeNovo') : t('ia.lerConversa')}
                        </MDButton>,
                        insights && resultBox(
                            <AiInsightsResult
                                key={reads}
                                leadId={leadId}
                                found={insights}
                                cc={cc}
                                onDiscard={() => setInsights(null)}
                                onApplied={() => {
                                    setInsights(null);
                                    onApplied?.();
                                }}
                            />
                        ),
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
